import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, take } from 'rxjs';

import {
  selectMePermissions,
  selectMePermissionsError,
} from '../Store/rbac-sod/rbac-sod.selectors';
import { FeatureAccessService } from './feature-access.service';
import { PermissionGateService } from './permission-gate.service';

/**
 * Route guard for the client-facing Savings Recommendations dashboard, which
 * reads the `pna_` tables the AI nuance pipeline writes.
 *
 * Two layers, mirroring `controlTowerGuard`:
 *
 *  1. Tenant enablement — the `tblPage` row for `savings-recommendations` must
 *     be present in the cached sidebar payload.
 *  2. Caller permission — the user must hold `savings.opportunities.report.view`.
 *
 * Layer 1 is also how LEGACY (non-RBAC) tenants are handled, and the handling is
 * "they do not get this page". A tenant DB without `tblRolePermission` /
 * `tblUserRole` returns no permissions, so layer 2 can never pass for them; the
 * backend waves such tenants through (`ITenantRbacModeDao.IsRbacTenant` → false
 * → skip the check) but the frontend has no equivalent notion, and `has$` is
 * plain set membership. Seed the `tblPage` row on RBAC tenants ONLY — with no
 * row, layer 1 redirects before layer 2 can bounce a legitimate user, and the
 * link never appears in their sidebar either. This is the same reason
 * `savingsTrackerGuard` and `reclassificationGuard` have never tripped on a
 * legacy tenant: no v2 page row, so the guard is never reached.
 *
 * `has$` default-denies until the MePermissions slice hydrates, so this waits
 * for the slice to settle (data OR error) before deciding — otherwise every cold
 * navigation would bounce a legitimate user. A load failure stays fail-closed:
 * no permission proven, no page.
 *
 * Denies land on `/dashboard`, the app's landing route.
 *
 * NOTE: this is the navigation boundary only. `SavingsRecommendationsController`
 * carries `[Authorize]` and no permission check, so the API still serves the
 * caller's own tenant data to any authenticated user. Gating the controller is a
 * separate change.
 */
export const savingsRecommendationsGuard: CanActivateFn = () => {
  const featureAccess = inject(FeatureAccessService);
  const permissionGate = inject(PermissionGateService);
  const store = inject(Store);
  const router = inject(Router);

  if (!featureAccess.isPageActive('savings-recommendations')) {
    router.navigateByUrl('dashboard');
    return false;
  }

  return combineLatest([
    permissionGate.has$('savings.opportunities.report.view'),
    store.select(selectMePermissions),
    store.select(selectMePermissionsError),
  ]).pipe(
    filter(([, me, error]) => me != null || error != null),
    take(1),
    map(([allowed]) => (allowed ? true : router.parseUrl('dashboard'))),
  );
};
